import { PtoV3DeployResult } from 'Modules'
import type { Address } from 'viem'

import { AppAdmin } from 'api/app-admin'
import { TokenizeStatusEnum } from 'constants/pto'
import { toCreatePtoParams } from 'extends/pto-v3.extend'
import { writeContractAndWait } from 'hooks/contract/use-contract-viem'
import { ORCHESTRATOR_NOT_CONFIGURED, usePtoOrchestrator } from 'hooks/pto-v3/use-pto-orchestrator'
import { logError, logInfo } from 'utils/logger'

/**
 * PTO v3 step 3 + 4: deploy the ERC-3643 PTO through PTOOrchestrator.createPTO with
 * the PTOParams the BE returns, then link the result on the BE.
 *
 * The call is simulated first: reverts (agent role, salt taken) surface before the
 * wallet is asked to sign, and the simulation yields the 3 addresses createPTO returns.
 */
export const useDeployPtoV3 = () => {
  const { orchestratorAddress, contract, isAgent } = usePtoOrchestrator()

  const deployPto = async (account: Address, id: string): Promise<PtoV3DeployResult> => {
    if (!orchestratorAddress || !contract) {
      throw new Error(ORCHESTRATOR_NOT_CONFIGURED)
    }

    const res = await AppAdmin.ptoV3GetCreateArgs(id)
    const args = res.data?.data
    if (!args?.salt || !args?.ownerAddress) {
      throw new Error('The API did not return the create-contract params')
    }
    const params = toCreatePtoParams(args)
    logInfo('PTO v3 createPTO params:', params)

    const { request, result } = await contract.simulate.createPTO([params], { account })
    const [ptoAddress, ptoIdentity, ptoCompliance] = result as [Address, Address, Address]

    const { hash } = await writeContractAndWait(request)

    return { ptoAddress, ptoIdentity, ptoCompliance, txHash: hash }
  }

  /**
   * Step 4 fallback - PATCH /admin/pto/:id/contract: contractAddress <- ptoAddress,
   * onchainId <- ptoIdentity. The BE hook already stores these from PTOCreated, so a
   * failure here is logged, not thrown. status is required by the endpoint: the create
   * form passes its Display switch, the Deploy button falls back to ACTIVE like v2.
   */
  const reportDeployed = async (
    id: string,
    result: PtoV3DeployResult,
    status: TokenizeStatusEnum = TokenizeStatusEnum.ACTIVE
  ) => {
    if (!result.ptoAddress) {
      throw new Error('createPTO did not return the PTO address')
    }
    try {
      await AppAdmin.ptoV3UpdateContract(id, {
        status,
        contractAddress: result.ptoAddress,
        onchainId: result.ptoIdentity,
      })
      return true
    } catch (error) {
      logError(
        'Fallback link of the deployed PTO on the BE failed (the hook will store it):',
        error
      )
      return false
    }
  }

  return {
    isAgent,
    orchestratorAddress,
    deployPto,
    reportDeployed,
  }
}
